import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { listEstimates } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, FilterBar, Button } from '../components/shared/ui'
import { currency, date } from '../lib/format'
import { useServerList } from '../lib/list'
import type { Estimate } from '../types'

const PAGE_SIZE = 25

export default function Estimates() {
  const navigate = useNavigate()
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort } =
    useServerList<Estimate>(listEstimates, { pageSize: PAGE_SIZE, defaultSort: { column: 'estimate_date', dir: 'desc' } })

  return (
    <div>
      <PageHeader title="Estimates" subtitle={`${count.toLocaleString()} total`}
        actions={<Button onClick={() => navigate('/estimates/new')}><Plus size={16} /> New Estimate</Button>} />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by estimate #, customer, or designer…" />
          <FilterBar
            onChange={setFilters}
            fields={[
              { key: 'date', label: 'Estimate Date', column: 'estimate_date', type: 'daterange' },
              { key: 'min_total', label: 'Min Total', column: 'estimate_total', type: 'number', placeholder: '0' },
            ]}
          />
        </div>
        {loading ? (
          <Spinner label="Loading estimates…" />
        ) : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={e => navigate(`/estimates/${e.id}`)}
              columns={[
                { key: 'legacy_id', header: 'Estimate #', render: e => <span className="font-medium text-gray-900">{e.bill_code ?? e.legacy_id ?? '—'}</span>, sortKey: 'legacy_id' },
                { key: 'customer_name', header: 'Customer', render: e => e.customer_name || '—', sortKey: 'customer_name' },
                { key: 'designer_name', header: 'Designer', render: e => e.designer_name || '—', sortKey: 'designer_name' },
                { key: 'estimate_date', header: 'Date', render: e => date(e.estimate_date), sortKey: 'estimate_date' },
                { key: 'estimate_total', header: 'Total', render: e => currency(e.estimate_total), className: 'text-right', sortKey: 'estimate_total' },
                { key: 'status', header: 'Status', render: e => e.invoice_number ? 'Invoiced' : 'Open', sortKey: 'invoice_number' },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  )
}
